import { useMemo, useState } from 'react';

import { uniqBy } from 'lodash-es';
import debounce from 'lodash-es/debounce';
import { DateTime } from 'luxon';

import { useAppSelector } from 'src/app/store/hooks';
import {
  AssignedDriverRead,
  useAssignmentsListQuery,
} from 'src/common/external/bambi-api/bambiApi';
import { ItemSelector } from 'src/common/ItemSelector/ItemSelector';
import { formatFullName } from 'src/common/util/formatFullName';

export default function DispatchCalendarAssignmentFilter({
  selectedDrivers,
  onChange,
}: {
  selectedDrivers: AssignedDriverRead[];
  onChange: (drivers: AssignedDriverRead[]) => void;
}) {
  const selectedDate = useAppSelector((state) => state.dispatcher.selectedDate);
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');

  const debouncedSetValue = useMemo(
    () => debounce((value: string) => setDebouncedSearchTerm(value), 300),
    []
  );
  const bod = DateTime.fromISO(selectedDate).startOf('day').toISO();
  const eod = DateTime.fromISO(selectedDate).endOf('day').toISO();

  // TODO: We could probably use the dispatch response and a client side search here
  const {
    drivers,
    isLoading: assignmentsLoading,
    isFetching: assignmentsFetching,
  } = useAssignmentsListQuery(
    {
      rangeStart: bod,
      rangeEnd: eod,
      search: debouncedSearchTerm,
    },
    {
      selectFromResult: (result) => {
        return {
          ...result,
          // Avoiding showing driver multiple times if they have multiple assignments
          // Like a split shift between two vehicles, etc
          drivers: uniqBy(
            result.data?.results.map((assignment) => assignment.driver),
            'id'
          ),
        };
      },
    }
  );

  return (
    <ItemSelector
      items={drivers}
      selectedItems={selectedDrivers}
      loading={assignmentsLoading}
      searching={assignmentsFetching}
      onSelectionChange={onChange}
      onSearchChange={debouncedSetValue}
      searchPlaceholder="Search drivers"
      getItemLabel={(driver) => formatFullName(driver)}
      dataTestId="assignment-search"
      noItemsText="No drivers found."
    />
  );
}
